import React, { useState, useEffect, useMemo, useRef } from "react";
import { db } from "./firebase";
import { doc, onSnapshot, updateDoc, setDoc } from "firebase/firestore";
import Loader from "./Loader";
import Notification from "./Notification";
import { v4 as uuidv4 } from "uuid";
import { removeUndefinedDeep } from "./helpers";
import {
  ArrowUp,
  ArrowDown,
  Plus,
  Trash2,
  GripVertical,
  X,
  Download,
  Upload,
  ChevronUp,
  ChevronDown,
  ChevronsUp,
  ChevronsDown,
  MoreVertical,
  Eye,
  EyeOff,
  Layers,
  Edit2,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const GOALS_DOC_ID = "currentGoals";
const PERIODS = ["weekly", "monthly", "yearly"];
const PERIOD_LABELS = { weekly: "Tyg", monthly: "Msc", yearly: "Roc" };
export const METHOD_OPTIONS = [
  { value: "average", label: "Średnia (im więcej tym lepiej)" },
  { value: "averageLow", label: "Średnia (im mniej tym lepiej)" },
  { value: "quantityMin", label: "Ilość (suma wystąpień)" },
  { value: "cap", label: "Suma wartości" },
  { value: "maxValue", label: "Maksymalna wartość" },
  { value: "daysMetThreshold", label: "Dni spełniające warunek" },
  { value: "streak", label: "Passa" }
];
const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9ąćęłńóśźż\s]/gi, '')
    .replace(/\s+/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_|_$/g, '');
// POLSKIE OPISE METOD
const METHOD_LABELS = {
  average: {
    label: "Średnia (im więcej tym lepiej)",
    desc: "Oblicza średnią z wpisów liczbowych – wyższy wynik to lepszy rezultat."
  },
  averageLow: {
    label: "Średnia (im mniej tym lepiej)",
    desc: "Oblicza średnią z wpisów liczbowych – niższy wynik to lepszy rezultat."
  },
  quantityMin: {
    label: "Ilość (suma wystąpień)",
    desc: "Sumuje wszystkie zaznaczenia TAK/checkboxy lub liczbę wystąpień w danym okresie."
  },
  cap: {
    label: "Suma wartości",
    desc: "Sumuje wartości wszystkich wpisów w wybranym okresie (np. liczba słów, dystans)."
  },
  maxValue: {
    label: "Maksymalna wartość",
    desc: "Największa wpisana wartość w danym okresie (np. rekord)."
  },
  daysMetThreshold: {
    label: "Dni spełniające warunek",
    desc: "Liczba dni, w których osiągnięto ustalony próg."
  },
  streak: {
    label: "Passa",
    desc: "Najdłuższa nieprzerwana passa dni z osiągnięciem celu."
  }
};



// --- UTILS
function highlight(text, query) {
  if (!query) return text;
  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <mark key={i} className="bg-yellow-300 dark:bg-yellow-700">{part}</mark>
    ) : (
      part
    )
  );
}

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [accordion, setAccordion] = useState({});
  const [modal, setModal] = useState({ open: false, mode: "", section: "", qId: null });
  const [form, setForm] = useState({});
  const [notif, setNotif] = useState("");
  const [history, setHistory] = useState([]);
  const [preview, setPreview] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState({});
  const fileInputRef = useRef(null);
  const searchRef = useRef(null);
  const [editQuestionId, setEditQuestionId] = useState(null);
  const [editQuestionValue, setEditQuestionValue] = useState("");
  const [sectionMenuOpen, setSectionMenuOpen] = useState({});
  const [deleteSectionModal, setDeleteSectionModal] = useState({ open: false, section: null });

function migrateCalcTypes(goals) {
  return goals.map(g => ({
    ...g,
    calcType:
      g.calcType === "sumCount" ? "cap"
      : g.calcType === "averageLower" || g.calcType === "averageMin" ? "averageLow"
      : g.calcType
  }));
}



  // CRUD & inne
  const removeSection = (sectionName) => {
    pushHistory();
    setSections(s => s.filter(sec => sec !== sectionName));
    setGoals(g => g.filter(q => q.category !== sectionName));
    notify(`Usunięto sekcję "${sectionName}"`);
  };

  const openModal = (mode, section = "", qId = null) => {
    setForm({});
    setModal({ open: true, mode, section, qId });
  };
  const closeModal = () => setModal({ open: false, mode: "", section: "", qId: null });

 useEffect(() => {
  const ref = doc(db, "goals", GOALS_DOC_ID);
  const unsub = onSnapshot(ref, snap => {
    if (snap.exists()) {
      const data = snap.data();
      // --- MIGRACJA
      const migratedGoals = migrateCalcTypes(data.goals || []);
      setGoals(migratedGoals);
      setSections(data.sections || [...new Set(migratedGoals.map(g => g.category))]);
    } else {
      setDoc(ref, { goals: [], sections: [] });
    }
    setLoading(false);
  });
  return () => unsub();
}, []);



  const notify = msg => {
    setNotif(msg);
    setTimeout(() => setNotif(""), 3000);
  };

  const pushHistory = () => setHistory(prev => [goals, ...prev].slice(0, 5));

  const addSection = () => {
    const title = form.title?.trim();
    if (title && !sections.includes(title)) {
      pushHistory();
      setSections(s => [...s, title]);
      notify("Sekcja dodana");
      closeModal();
    }
  };

  const addQuestion = () => {
    const text = form.question?.trim();
    if (!text) return;
 const newQ = {
  id: uuidv4(),
  category: modal.section,
  question: text,
  uid: slugify(text),
  entryType: form.entryType || "number",
  calcType: form.calcType || METHOD_OPTIONS[0].value,
  weekly: { min: 0, opt: 0 },
  monthly: { min: 0, opt: 0 },
  yearly: { min: 0, opt: 0 }
};

    pushHistory();
    setGoals(g => [...g, newQ]);
    if (!sections.includes(modal.section)) setSections(s => [...s, modal.section]);
    notify("Pytanie dodane");
    closeModal();
  };

  const updateQuestionMethods = () => {
    const { qId } = modal;
    pushHistory();
    setGoals(g =>
      g.map(x =>
        x.id === qId
          ? { ...x, entryType: form.entryType, calcType: form.calcType }
          : x
      )
    );
    notify("Zaktualizowano");
    closeModal();
  };

  const removeQ = id => {
    pushHistory();
    setGoals(g => g.filter(x => x.id !== id));
    notify("Pytanie usunięte");
  };

  const updateTh = (id, period, key, value) => {
    pushHistory();
    setGoals(g =>
      g.map(x =>
        x.id === id
          ? { ...x, [period]: { ...x[period], [key]: value === "" ? "" : Math.max(0, Math.min(9999, Number(value))) } }
          : x
      )
    );
  };

  // Drag & Drop
  const onDragEnd = result => {
    if (!result.destination) return;
    pushHistory();
    const { source, destination, type } = result;
    if (type === "SECTION") {
      const arr = [...sections];
      const [moved] = arr.splice(source.index, 1);
      arr.splice(destination.index, 0, moved);
      setSections(arr);
    } else {
      const sec = source.droppableId;
      const list = [...goals.filter(g => g.category === sec)];
      const [moved] = list.splice(source.index, 1);
      list.splice(destination.index, 0, moved);
      setGoals(prev => [
        ...prev.filter(g => g.category !== sec),
        ...list
      ]);
    }
  };

  // Move section via buttons
  const moveSection = (sec, dir) => {
    pushHistory();
    setSections(s => {
      const i = s.indexOf(sec);
      const arr = [...s];
      if (dir === "up" && i > 0) [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
      if (dir === "down" && i < arr.length - 1) [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]];
      return arr;
    });
  };

  // Export / Import
  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(goals, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "goals.json"; a.click();
    URL.revokeObjectURL(url);
  };
  const importJSON = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const arr = JSON.parse(ev.target.result);
        if (Array.isArray(arr)) {
          pushHistory();
          setGoals(arr);
          setSections([...new Set(arr.map(x => x.category))]);
          notify("Zaimportowano");
        }
      } catch {
        notify("Błąd importu");
      }
    };
    reader.readAsText(file);
  };

 const saveAll = async () => {
  const cleanGoals = removeUndefinedDeep(goals);
  const cleanSections = removeUndefinedDeep(sections);
  await updateDoc(doc(db, "goals", GOALS_DOC_ID), { goals: cleanGoals, sections: cleanSections });
  window.scrollTo({ top: 0, behavior: "smooth" });
  notify("Zapisano zmiany");
};


  // Group & filter
  const grouped = useMemo(() => {
    const map = {};
    sections.forEach(sec => {
      map[sec] = goals.filter(
        g => g.category === sec && g.question.toLowerCase().includes(search.toLowerCase())
      );
    });
    return map;
  }, [goals, sections, search]);
  const visible = search ? sections.filter(s => grouped[s].length) : sections;

  // Auto-expand on search
  useEffect(() => {
    if (!search) return;
    setAccordion(prev => {
      const next = { ...prev };
      visible.forEach(sec => {
        if (grouped[sec].some(q => q.question.toLowerCase().includes(search.toLowerCase()))) {
          next[sec] = true;
        }
      });
      return next;
    });
  }, [search, grouped, visible]);

  // Accordion controls
  const allOpen = visible.every(sec => accordion[sec]);
  const toggleAll = () => {
    setAccordion(prev => {
      const next = {};
      visible.forEach(sec => (next[sec] = !allOpen));
      return next;
    });
  };
  const toggleSection = sec => setAccordion(prev => ({ ...prev, [sec]: !prev[sec] }));

  if (loading) return <Loader />;

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="max-w-3xl mx-auto p-4 space-y-6">
        {notif && <Notification message={notif} />}

        <div className="flex flex-col sm:flex-row gap-2 items-stretch mb-4">
          {/* Search */}
          <div className="flex-1">
            <input
              ref={searchRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Szukaj pytania..."
              className="w-full px-3 py-2 border border-green-200 rounded-lg focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 outline-none dark:bg-gray-700 dark:border-gray-600 transition"
            />
          </div>
          {/* Toolbar */}
          <div className="flex flex-wrap gap-2 justify-end items-center flex-1 bg-white dark:bg-gray-800 rounded-lg p-2 shadow-sm">
            <button
              onClick={() => openModal("section")}
              className="flex items-center px-3 py-1 bg-green-100 hover:bg-green-200 text-green-600 dark:bg-emerald-900 dark:hover:bg-emerald-800 dark:text-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 transition"
              title="Dodaj nową sekcję celów"
            >
              <Plus className="mr-1" /> Nowa sekcja
            </button>
            <button
              onClick={exportJSON}
              className="p-1 bg-green-100 hover:bg-green-200 text-green-600 dark:bg-emerald-900 dark:hover:bg-emerald-800 dark:text-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 transition"
              title="Eksportuj cele do pliku (JSON)"
            >
              <Download className="text-green-600 dark:text-emerald-300" />
            </button>
            <button
              onClick={() => fileInputRef.current.click()}
              className="p-1 bg-green-100 hover:bg-green-200 text-green-600 dark:bg-emerald-900 dark:hover:bg-emerald-800 dark:text-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 transition"
              title="Importuj cele z pliku (JSON)"
            >
              <Upload className="text-green-600 dark:text-emerald-300" />
            </button>
            <button
              onClick={() => setPreview(p => !p)}
              className="flex items-center px-3 py-1 bg-green-100 hover:bg-green-200 text-green-600 dark:bg-emerald-900 dark:hover:bg-emerald-800 dark:text-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500 transition"
              title={preview ? "Ukryj podgląd pytań" : "Pokaż podgląd pytań"}
            >
              {preview
                ? (<><EyeOff className="mr-1" /> Ukryj</>)
                : (<><Eye className="mr-1" /> Podgląd</>)
              }
            </button>
          </div>
        </div>
        <input ref={fileInputRef} type="file" accept="application/json" onChange={importJSON} className="hidden" />

        {/* Toggle all */}
        <div className="flex items-center border-t pt-2">
          <button
            onClick={toggleAll}
            className="
              ml-auto flex items-center gap-2
              px-3 py-1
              bg-green-100 hover:bg-green-200
              dark:bg-emerald-900 dark:hover:bg-emerald-800
              text-green-600 dark:text-emerald-300
              rounded-lg transition text-sm
            "
            title={allOpen ? 'Zwiń wszystkie sekcje' : 'Rozwiń wszystkie sekcje'}
          >
            {allOpen ? (
              <>
                <ChevronsUp size={18} className="text-green-600 dark:text-emerald-300" />
                <span>Zwiń wszystkie</span>
              </>
            ) : (
              <>
                <ChevronsDown size={18} className="text-green-600 dark:text-emerald-300" />
                <span>Rozwiń wszystkie</span>
              </>
            )}
          </button>
        </div>

        {/* Sections */}
        <Droppable droppableId="all" type="SECTION">
          {prov => (
            <div {...prov.droppableProps} ref={prov.innerRef} className="space-y-4">
              {visible.map((sec, idx) => (
                <div key={sec}>
                  {/* Section header */}
                  <div className="flex items-center justify-between py-3 border-b border-gray-300 dark:border-gray-600 hover:bg-emerald-50 dark:hover:bg-emerald-900 transition">
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleSection(sec)} className="flex items-center gap-2">
                        <Layers className="text-emerald-600" size={20} />
                        <h2 className="text-xl font-bold text-emerald-800 dark:text-emerald-200">{sec}</h2>
                      </button>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={e => { e.stopPropagation(); moveSection(sec, 'up'); }}
                        disabled={sections.indexOf(sec) === 0}
                        className="p-1 rounded hover:bg-gray-100 transition"
                        title="Przesuń sekcję wyżej"
                      >
                        <ArrowUp className={`text-gray-400 ${sections.indexOf(sec) === 0 ? 'opacity-50' : ''}`} size={20} />
                      </button>
                      <button
                        onClick={e => { e.stopPropagation(); moveSection(sec, 'down'); }}
                        disabled={sections.indexOf(sec) === sections.length - 1}
                        className="p-1 rounded hover:bg-gray-100 transition"
                        title="Przesuń sekcję niżej"
                      >
                        <ArrowDown className={`text-gray-400 ${sections.indexOf(sec) === sections.length - 1 ? 'opacity-50' : ''}`} size={20} />
                      </button>
                      <div className="w-px h-6 bg-gray-200 mx-2"></div>
                      <button
                        onClick={e => { e.stopPropagation(); toggleSection(sec); }}
                        className="p-1 rounded hover:bg-emerald-200 transition"
                        title={accordion[sec] ? "Zwiń pytania" : "Rozwiń pytania"}
                      >
                        {accordion[sec]
                          ? <ChevronUp className="text-emerald-600" size={20} />
                          : <ChevronDown className="text-emerald-600" size={20} />}
                      </button>
                    </div>
                  </div>

                  {/* Questions table + pasek akcji */}
                  {accordion[sec] && (
                    <div>
                      <div className="flex gap-2 justify-end mb-2">
                        <button
                          onClick={() => openModal("question", sec)}
                          className="flex items-center px-3 py-1 bg-green-100 hover:bg-green-200 dark:bg-emerald-900 dark:hover:bg-emerald-800 text-green-600 dark:text-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-400 transition text-sm"
                          title="Dodaj pytanie do tej sekcji"
                        >
                          <Plus className="mr-1" size={18} /> Dodaj pytanie
                        </button>
                        <button
                          onClick={() => setDeleteSectionModal({ open: true, section: sec })}
                          className="flex items-center px-3 py-1 bg-red-50 hover:bg-red-100 dark:bg-red-900 dark:hover:bg-red-800 text-red-600 dark:text-red-400 rounded-lg focus:ring-2 focus:ring-red-400 transition text-sm"
                          title="Usuń całą sekcję"
                        >
                          <Trash2 className="mr-1" size={18} /> Usuń sekcję
                        </button>
                      </div>
                      <Droppable droppableId={sec} type="QUESTION">
                        {(inner, snap) => (
                          <table className={`w-full bg-white dark:bg-gray-800 rounded-lg overflow-hidden ${snap.isDraggingOver ? "bg-emerald-50 dark:bg-emerald-900" : ""}`}>
                            <thead>
                              <tr className="bg-green-200 text-green-800 dark:bg-emerald-900 dark:text-emerald-300">
                                <th className="w-8"></th>
                                <th className="p-2 text-left">Pytanie</th>
                                {PERIODS.map(p => <th key={p} colSpan={2} className="p-1 text-center">{PERIOD_LABELS[p]}</th>)}
                                <th className="w-8"></th>
                              </tr>
                              <tr className="bg-green-100 text-green-700 dark:bg-emerald-800 dark:text-emerald-200">
                                <th className="w-8"></th>
                                <th className="p-2"></th>
                                {PERIODS.map(p => (
                                  <React.Fragment key={p}>
                                    <th className="p-1 text-center">Min</th>
                                    <th className="p-1 text-center">Opt</th>
                                  </React.Fragment>
                                ))}
                                <th className="w-8"></th>
                              </tr>
                            </thead>
                            <tbody {...inner.droppableProps} ref={inner.innerRef} className="divide-y divide-gray-200 dark:divide-gray-700">
                              {preview && (
                                <tr>
                                  <td colSpan={PERIODS.length * 2 + 3} className="p-2 text-center text-gray-500 dark:text-gray-400">●●●</td>
                                </tr>
                              )}
                              {grouped[sec].map((q, j) => (
                                <Draggable key={q.id} draggableId={q.id} index={j}>
                                  {row => {
                                    const isMatch = search && q.question.toLowerCase().includes(search.toLowerCase());
                                    const isLast = j === grouped[sec].length - 1;

                                    // TEXTAREA
                                    if (q.entryType === "textarea") {
                                      return (
                                        <tr
                                          ref={row.innerRef}
                                          {...row.draggableProps}
                                          className="hover:bg-emerald-50 dark:hover:bg-emerald-900"
                                        >
                                          <td {...row.dragHandleProps} className="p-2 text-center align-top">
                                            <GripVertical className="text-gray-500" size={16} />
                                          </td>
                                          <td
                                            colSpan={PERIODS.length * 2 + 1}
                                            className="p-2 align-top"
                                          >
                                            <div className="flex items-center gap-2">
                                              {editQuestionId === q.id ? (
                                                <textarea
                                                  autoFocus
                                                  rows={Math.max(2, (editQuestionValue.match(/\n/g) || []).length + 1)}
                                                  value={editQuestionValue}
                                                  onChange={e => setEditQuestionValue(e.target.value)}
                                                  onBlur={() => {
                                                    if (editQuestionValue.trim()) {
                                                      setGoals(goals =>
                                                        goals.map(x => x.id === q.id ? { ...x, question: editQuestionValue } : x)
                                                      );
                                                    }
                                                    setEditQuestionId(null);
                                                  }}
                                                  onKeyDown={e => {
                                                    if (e.key === "Enter" && !e.shiftKey) e.target.blur();
                                                  }}
                                                  className="w-full min-h-[38px] max-h-[180px] p-1 border border-green-300 rounded focus:ring-2 focus:ring-emerald-400 dark:bg-gray-700 resize-vertical"
                                                  style={{ fontSize: "1rem" }}
                                                />
                                              ) : (
                                                <span
                                                  onClick={() => {
                                                    setEditQuestionId(q.id);
                                                    setEditQuestionValue(q.question);
                                                  }}
                                                  className="cursor-pointer hover:bg-green-50 dark:hover:bg-emerald-900 rounded transition px-1 py-1 flex items-center text-gray-900 dark:text-gray-100"
                                                  title="Kliknij, aby edytować"
                                                >
                                                  <span className="flex-1">{isMatch ? highlight(q.question, search) : q.question}</span>
                                                  <Edit2 className="ml-2 text-emerald-400 opacity-0 group-hover:opacity-100 transition" size={16} />
                                                </span>
                                              )}
                                            </div>
                                          </td>
                                          <td className="p-2 text-center relative align-top">
                                            <button
                                              onClick={() => setDetailsOpen(d => ({ ...d, [q.id]: !d[q.id] }))}
                                              className="p-1 rounded hover:bg-green-50 dark:hover:bg-green-900"
                                              aria-label="Więcej"
                                            >
                                              <MoreVertical className="text-gray-500" size={16} />
                                            </button>
                                            {detailsOpen[q.id] && (
                                              <>
                                                <div
                                                  onClick={() => setDetailsOpen(d => ({ ...d, [q.id]: false }))}
                                                  className="fixed inset-0 z-30"
                                                  style={{ background: "transparent" }}
                                                />
                                                <div
                                                  className={`absolute right-0 ${isLast ? "bottom-0 mb-6" : "top-0 mt-6"
                                                    } min-w-[120px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-2 flex flex-col gap-1 z-40 animate-fadeIn`}
                                                >
                                                  <button
                                                    onClick={() => {
                                                      openModal("edit", sec, q.id);
                                                      setDetailsOpen(d => ({ ...d, [q.id]: false }));
                                                    }}
                                                    className="flex items-center gap-2 px-3 py-1 text-sm rounded hover:bg-green-50 dark:hover:bg-green-900 transition"
                                                  >
                                                    <Edit2 size={14} /> Edytuj
                                                  </button>
                                                  <button
                                                    onClick={() => {
                                                      removeQ(q.id);
                                                      setDetailsOpen(d => ({ ...d, [q.id]: false }));
                                                    }}
                                                    className="flex items-center gap-2 px-3 py-1 text-sm rounded hover:bg-red-100 dark:hover:bg-red-900 text-red-600 transition"
                                                  >
                                                    <Trash2 size={14} /> Usuń
                                                  </button>
                                                </div>
                                              </>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    }

                                    // NUMBER / CHECKBOX (standard)
                                    return (
                                      <tr ref={row.innerRef} {...row.draggableProps} className="hover:bg-emerald-50 dark:hover:bg-emerald-900">
                                        <td {...row.dragHandleProps} className="p-2 text-center">
                                          <GripVertical className="text-gray-500" size={16} />
                                        </td>
                                        <td className="pl-2 pr-1 max-w-[400px] break-words align-middle group relative">
                                          {editQuestionId === q.id ? (
                                            <textarea
                                              autoFocus
                                              rows={Math.max(2, (editQuestionValue.match(/\n/g) || []).length + 1)}
                                              value={editQuestionValue}
                                              onChange={e => setEditQuestionValue(e.target.value)}
                                              onBlur={() => {
                                                if (editQuestionValue.trim()) {
                                                  setGoals(goals =>
                                                    goals.map(x => x.id === q.id ? { ...x, question: editQuestionValue } : x)
                                                  );
                                                }
                                                setEditQuestionId(null);
                                              }}
                                              onKeyDown={e => {
                                                if (e.key === "Enter" && !e.shiftKey) e.target.blur();
                                              }}
                                              className="w-full min-h-[38px] max-h-[180px] p-1 border border-green-300 rounded focus:ring-2 focus:ring-emerald-400 dark:bg-gray-700 resize-vertical"
                                              style={{ fontSize: "1rem" }}
                                            />
                                          ) : (
                                            <span
                                              onClick={() => {
                                                setEditQuestionId(q.id);
                                                setEditQuestionValue(q.question);
                                              }}
                                              className="cursor-pointer hover:bg-green-50 dark:hover:bg-emerald-900 rounded transition px-1 py-1 flex items-center text-gray-900 dark:text-gray-100"
                                              title="Kliknij, aby edytować"
                                            >
                                              <span className="flex-1">{isMatch ? highlight(q.question, search) : q.question}</span>
                                              <Edit2 className="ml-2 text-emerald-400 opacity-0 group-hover:opacity-100 transition" size={16} />
                                            </span>
                                          )}
                                        </td>
                                        {PERIODS.map(p => (
                                          <React.Fragment key={p}>
                                            <td className="p-2 text-center">
                                              <input
                                                type="number"
                                                max={9999}
                                                min={0}
                                                inputMode="numeric"
                                                maxLength={4}
                                                value={q[p].min}
                                                onChange={e => {
                                                  const val = e.target.value;
                                                  updateTh(q.id, p, "min", val === "" ? "" : Math.max(0, Math.min(9999, Number(val))));
                                                }}
                                                className="w-12 text-center border border-green-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-gray-900 dark:text-gray-100 dark:bg-gray-800 dark:border-gray-600 dark:placeholder-gray-400"
                                                style={{
                                                  boxShadow: '0 0 0 2px #10b98133',
                                                  caretColor: '#10b981',
                                                }}
                                              />
                                            </td>
                                            <td className="p-2 text-center">
                                              <input
                                                type="number"
                                                max={9999}
                                                min={0}
                                                inputMode="numeric"
                                                maxLength={4}
                                                value={q[p].opt}
                                                onChange={e => {
                                                  const val = e.target.value;
                                                  updateTh(q.id, p, "opt", val === "" ? "" : Math.max(0, Math.min(9999, Number(val))));
                                                }}
                                                className="w-12 text-center border border-green-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-gray-900 dark:text-gray-100 dark:bg-gray-800 dark:border-gray-600 dark:placeholder-gray-400"
                                                style={{
                                                  boxShadow: '0 0 0 2px #10b98133',
                                                  caretColor: '#10b981',
                                                }}
                                              />
                                            </td>
                                          </React.Fragment>
                                        ))}
                                        <td className="p-2 text-center relative">
                                          <button
                                            onClick={() => setDetailsOpen(d => ({ ...d, [q.id]: !d[q.id] }))}
                                            className="p-1 rounded hover:bg-green-50 dark:hover:bg-green-900"
                                            aria-label="Więcej"
                                          >
                                            <MoreVertical className="text-gray-500" size={16} />
                                          </button>
                                          {detailsOpen[q.id] && (
                                            <>
                                              <div
                                                onClick={() => setDetailsOpen(d => ({ ...d, [q.id]: false }))}
                                                className="fixed inset-0 z-30"
                                                style={{ background: "transparent" }}
                                              />
                                              <div
                                                className={`absolute right-0 ${isLast ? "bottom-0 mb-6" : "top-0 mt-6"
                                                  } min-w-[120px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-2 flex flex-col gap-1 z-40 animate-fadeIn`}
                                              >
                                                <button
                                                  onClick={() => {
                                                    openModal("edit", sec, q.id);
                                                    setDetailsOpen(d => ({ ...d, [q.id]: false }));
                                                  }}
                                                  className="flex items-center gap-2 px-3 py-1 text-sm rounded hover:bg-green-50 dark:hover:bg-green-900 transition"
                                                >
                                                  <Edit2 size={14} /> Edytuj
                                                </button>
                                                <button
                                                  onClick={() => {
                                                    removeQ(q.id);
                                                    setDetailsOpen(d => ({ ...d, [q.id]: false }));
                                                  }}
                                                  className="flex items-center gap-2 px-3 py-1 text-sm rounded hover:bg-red-100 dark:hover:bg-red-900 text-red-600 transition"
                                                >
                                                  <Trash2 size={14} /> Usuń
                                                </button>
                                              </div>
                                            </>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  }}
                                </Draggable>
                              ))}
                              {inner.placeholder}
                            </tbody>
                          </table>
                        )}
                      </Droppable>
                    </div>
                  )}
                </div>
              ))}
              {prov.placeholder}
            </div>
          )}
        </Droppable>

        {/* Save/Undo */}
        <div className="flex justify-center gap-4">
          <button
            onClick={() => history.length && setGoals(history[0])}
            disabled={!history.length}
            className="px-4 py-2 bg-gray-300 dark:bg-gray-600 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 focus:outline-none"
          >
            Cofnij
          </button>
          <button
            onClick={saveAll}
            className="px-6 py-2 bg-emerald-500 dark:bg-emerald-600 text-white rounded-lg hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-400"
          >
            Zapisz zmiany
          </button>
        </div>

        {/* Modals */}
        {modal.open && (
          <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-80 relative">
              <button onClick={closeModal} className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100">
                <X size={20} />
              </button>
              {modal.mode === "section" && (
                <>
                  <h3 className="text-lg font-semibold mb-4 dark:text-gray-100">Nowa sekcja</h3>
                  <input
                    autoFocus
                    value={form.title || ""}
                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="Tytuł sekcji"
                    className="w-full p-2 border border-green-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-green-400 dark:bg-gray-700"
                  />
                  <div className="flex justify-end gap-2">
                    <button onClick={closeModal} className="px-3 py-1 bg-gray-300 rounded-lg hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500">
                      Anuluj
                    </button>
                    <button onClick={addSection} className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600">
                      Dodaj
                    </button>
                  </div>
                </>
              )}
              {modal.mode === "question" && (
                <>
                  <h3 className="text-lg font-semibold mb-4 dark:text-gray-100">Nowe pytanie w "{modal.section}"</h3>
                  <textarea
                    autoFocus
                    rows={4}
                    value={form.question || ""}
                    onChange={e => setForm(f => ({ ...f, question: e.target.value }))}
                    placeholder="Treść pytania"
                    className="w-full p-2 border border-green-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-green-400 dark:bg-gray-700"
                  />
                  <select
                    value={form.entryType || "number"}
                    onChange={e => setForm(f => ({ ...f, entryType: e.target.value }))}
                    className="w-full p-2 border border-green-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-green-400 dark:bg-gray-700"
                  >
                    <option value="checkbox">Check (Tak/Nie)</option>
                    <option value="number">Ilość/liczba</option>
                    <option value="textarea">Pole tekstowe</option>
                  </select>
                  {form.entryType !== "textarea" && (
  <>
    <select
      value={form.calcType || METHOD_OPTIONS[0].value}
      onChange={e => setForm(f => ({ ...f, calcType: e.target.value }))}
      className="w-full p-2 border border-green-300 rounded-lg mb-1 focus:outline-none focus:ring-2 focus:ring-green-400 dark:bg-gray-700"
    >
      {METHOD_OPTIONS.map(o => (
        <option key={o.value} value={o.value}>{METHOD_LABELS[o.value]?.label || o.label}</option>
      ))}
    </select>
    <div className="mb-4 text-gray-500 dark:text-gray-400 text-xs ml-1">
      {METHOD_LABELS[form.calcType || METHOD_OPTIONS[0].value]?.desc}
    </div>
  </>
)}

                  <div className="flex justify-end gap-2 mt-2">
                    <button onClick={closeModal} className="px-3 py-1 bg-gray-300 rounded-lg hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500">
                      Anuluj
                    </button>
                    <button onClick={addQuestion} className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600">
                      Dodaj
                    </button>
                  </div>
                </>
              )}
              {/* Modal EDYCJI z tłumaczeniem metody */}
              {modal.mode === "edit" && (() => {
                const currGoal = goals.find(x => x.id === modal.qId) || {};
                const currentEntryType = currGoal.entryType || currGoal.type || "number";
                const currentCalcType = currGoal.calcType || "quantityMin";
                const selectedEntryType = form.entryType || currentEntryType;
                const selectedCalcType = form.calcType || currentCalcType;
                const isChangedType = selectedEntryType !== currentEntryType;
                const isChangedCalc = selectedCalcType !== currentCalcType;

                // Ładne tłumaczenia
                const ENTRY_TYPE_LABELS = {
                  checkbox: "Check (Tak/Nie)",
                  number: "Ilość/liczba",
                  textarea: "Pole tekstowe"
                };
                const CALC_TYPE_LABELS = {
                average: "Średnia (im więcej tym lepiej)",
  averageLow: "Średnia (im mniej tym lepiej)",
  quantityMin: "Ilość (suma wystąpień)",
  cap: "Suma wartości",
  maxValue: "Maksymalna wartość",
  daysMetThreshold: "Dni spełniające warunek",
  streak: "Passa"
                };

                return (
                  <>
                    <h3 className="text-lg font-semibold mb-4 dark:text-gray-100">Edytuj metody pytania</h3>
                    {/* AKTUALNY TYP */}
                    <div className="mb-1 text-sm">
                      <span className="text-gray-700 dark:text-gray-200 font-semibold">Aktualny typ odpowiedzi:</span>{" "}
                      <span className="inline-block bg-emerald-100 dark:bg-emerald-800 px-2 py-1 rounded font-medium text-emerald-700 dark:text-emerald-300 mr-1">
                        {ENTRY_TYPE_LABELS[currentEntryType] || currentEntryType}
                      </span>
                      {isChangedType &&
                        <span className="inline-block ml-2 bg-yellow-100 dark:bg-yellow-900 px-2 py-1 rounded font-medium text-yellow-800 dark:text-yellow-300">
                          Nowy: {ENTRY_TYPE_LABELS[selectedEntryType] || selectedEntryType}
                        </span>
                      }
                    </div>
                    {/* AKTUALNA METODA */}
                    {selectedEntryType !== "textarea" && (
  <div className="mb-2 text-sm">
    <span className="text-gray-700 dark:text-gray-200 font-semibold">Aktualna metoda wyliczania:</span>{" "}
    <span className="inline-block bg-emerald-100 dark:bg-emerald-800 px-2 py-1 rounded font-medium text-emerald-700 dark:text-emerald-300 mr-1">
      {CALC_TYPE_LABELS[currentCalcType] || currentCalcType}
    </span>
    {isChangedCalc &&
      <span className="inline-block ml-2 bg-yellow-100 dark:bg-yellow-900 px-2 py-1 rounded font-medium text-yellow-800 dark:text-yellow-300">
        Nowa: {CALC_TYPE_LABELS[selectedCalcType] || selectedCalcType}
      </span>
    }
  </div>
)}

                    {/* SELECTY */}
                    <select
                      value={selectedEntryType}
                      onChange={e => setForm(f => ({ ...f, entryType: e.target.value }))}
                      className="w-full p-2 border border-green-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-green-400 dark:bg-gray-700"
                    >
                      <option value="checkbox">Check (Tak/Nie)</option>
                      <option value="number">Ilość/liczba</option>
                      <option value="textarea">Pole tekstowe</option>
                    </select>
                    {selectedEntryType !== "textarea" && (
  <>
    <select
      value={selectedCalcType}
      onChange={e => setForm(f => ({ ...f, calcType: e.target.value }))}
      className="w-full p-2 border border-green-300 rounded-lg mb-1 focus:outline-none focus:ring-2 focus:ring-green-400 dark:bg-gray-700"
    >
      {METHOD_OPTIONS.map(o => (
        <option key={o.value} value={o.value}>{METHOD_LABELS[o.value]?.label || o.label}</option>
      ))}
    </select>
    <div className="mb-4 text-gray-500 dark:text-gray-400 text-xs ml-1">
      {METHOD_LABELS[selectedCalcType]?.desc}
    </div>
  </>
)}

                    <div className="flex justify-end gap-2 mt-2">
                      <button onClick={closeModal} className="px-3 py-1 bg-gray-300 rounded-lg hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500">
                        Anuluj
                      </button>
                      <button onClick={updateQuestionMethods} className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600">
                        Zapisz
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}
        {deleteSectionModal.open && (
          <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-80 relative">
              <button
                onClick={() => setDeleteSectionModal({ open: false, section: null })}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
                aria-label="Zamknij"
              >
                <X size={20} />
              </button>
              <h3 className="text-lg font-semibold mb-4 dark:text-gray-100">
                Potwierdź usunięcie sekcji
              </h3>
              <div className="mb-4 text-gray-700 dark:text-gray-200">
                <span>
                  Na pewno chcesz usunąć sekcję <b className="text-emerald-700 dark:text-emerald-300">"{deleteSectionModal.section}"</b>?
                </span>
                <div className="mt-2 text-sm text-red-600 dark:text-red-400 font-medium">
                  Wszystkie jej pytania zostaną usunięte!
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setDeleteSectionModal({ open: false, section: null })}
                  className="px-3 py-1 bg-gray-300 rounded-lg hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-900 dark:text-gray-100"
                >
                  Anuluj
                </button>
                <button
                  onClick={() => {
                    removeSection(deleteSectionModal.section);
                    setDeleteSectionModal({ open: false, section: null });
                  }}
                  className="px-4 py-1 bg-red-500 hover:bg-red-600 text-white rounded-lg"
                >
                  Usuń
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </DragDropContext>
  );
}
